import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, Calculator, Users, Info } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import axios from "axios";

interface EmployeeData {
  _id: string;
  fullName: string;
  email: string;
  designation?: string;
  department?: string;
  totalPaidLeaves?: number;
  remainingPaidLeaves?: number;
  isActive: boolean;
  resignationDate?: string;
}

interface AttendanceFormData {
  workingDays: string;
  paidLeaves: string;
  unpaidLeaves: string;
}

export default function AddEditAttendance() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = window.location.pathname.includes("/edit/");

  const month = searchParams.get("month") || new Date().getMonth() + 1;
  const year = searchParams.get("year") || new Date().getFullYear();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingEmployee, setIsFetchingEmployee] = useState(false);
  const [isFetchingAttendance, setIsFetchingAttendance] = useState(false);
  const [error, setError] = useState("");
  const [employee, setEmployee] = useState<EmployeeData | null>(null);

  const [formData, setFormData] = useState<AttendanceFormData>({
    workingDays: "",
    paidLeaves: "",
    unpaidLeaves: "",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    totalDays: 0,
    presentDays: 0,
    remainingPaidLeaves: 0,
  });

  useEffect(() => {
    if (id) {
      fetchEmployeeData(id);
      // Always fetch attendance data to get auto-calculated values
      // This works for both add and edit modes - API returns auto-calculated values even if attendance doesn't exist
      fetchAttendanceData(id);
    }
  }, [id, isEditMode, month, year]);

  useEffect(() => {
    calculateTotals();
  }, [formData, employee]);

  const fetchEmployeeData = async (employeeId: string) => {
    try {
      setIsFetchingEmployee(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/${employeeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEmployee(response.data);
    } catch (error: any) {
      console.error("Error fetching employee:", error);
      setError(
        error.response?.data?.message || "Failed to fetch employee data"
      );
    } finally {
      setIsFetchingEmployee(false);
    }
  };

  const fetchAttendanceData = async (employeeId: string) => {
    try {
      setIsFetchingAttendance(true);

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/attendance/${employeeId}/${year}/${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const attendanceData = response.data;
      
      // If attendance doesn't exist, API returns auto-calculated values
      // Use autoCalculated values if available, otherwise use the direct values
      const paidLeaves = attendanceData.autoCalculated?.paidLeaves ?? attendanceData.paidLeaves ?? 0;
      const unpaidLeaves = attendanceData.autoCalculated?.unpaidLeaves ?? attendanceData.unpaidLeaves ?? 0;
      
      setFormData({
        workingDays: attendanceData.workingDays?.toString() || "",
        paidLeaves: paidLeaves.toString(),
        unpaidLeaves: unpaidLeaves.toString(),
      });
    } catch (error: any) {
      console.error("Error fetching attendance:", error);
      // If 404, try to get auto-calculated values (API should return them even for 404)
      // But if it's a real error, show it
      if (error.response?.status === 404) {
        // API should return auto-calculated values even for 404, but if it doesn't,
        // we'll just leave the form empty and let user fill it manually
        setFormData({
          workingDays: "",
          paidLeaves: "",
          unpaidLeaves: "",
        });
      } else {
        setError(
          error.response?.data?.message || "Failed to fetch attendance data"
        );
      }
    } finally {
      setIsFetchingAttendance(false);
    }
  };

  const calculateTotals = () => {
    const workingDays = parseInt(formData.workingDays) || 0;
    const paidLeaves = parseInt(formData.paidLeaves) || 0;
    const unpaidLeaves = parseInt(formData.unpaidLeaves) || 0;

    const totalDays = workingDays + paidLeaves + unpaidLeaves;
    const presentDays = workingDays + paidLeaves;
    
    // The employee's remainingPaidLeaves already accounts for approved leaves that were deducted
    // The paidLeaves in the attendance form represents leaves that were already approved and deducted
    // So we use the current remainingPaidLeaves directly - no need to subtract again
    // The attendance record is just documenting when those approved leaves were taken
    const remainingPaidLeaves = employee?.remainingPaidLeaves || 0;

    setCalculatedValues({
      totalDays,
      presentDays,
      remainingPaidLeaves,
    });
  };

  const handleInputChange = (
    field: keyof AttendanceFormData,
    value: string
  ) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({
      ...prev,
      [field]: numericValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const workingDays = parseInt(formData.workingDays) || 0;
      const paidLeaves = parseInt(formData.paidLeaves) || 0;
      const unpaidLeaves = parseInt(formData.unpaidLeaves) || 0;

      if (workingDays === 0 && paidLeaves === 0 && unpaidLeaves === 0) {
        setError("At least one attendance field must be greater than 0");
        return;
      }

      const attendanceData = {
        month: parseInt(month.toString()),
        year: parseInt(year.toString()),
        workingDays,
        paidLeaves,
        unpaidLeaves,
      };

      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}api/attendance/${id}`,
        attendanceData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const successMessage = isEditMode
          ? "Attendance updated successfully!"
          : "Attendance added successfully!";

        navigate("/attendance", {
          state: { message: successMessage },
        });
      }
    } catch (error: any) {
      console.error(
        `Error ${isEditMode ? "updating" : "adding"} attendance:`,
        error
      );
      setError(
        error.response?.data?.message ||
          `Failed to ${isEditMode ? "update" : "add"} attendance`
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetchingEmployee || isFetchingAttendance) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div className="text-center">
              <h3 className="text-lg font-medium">
                {isFetchingEmployee
                  ? "Loading employee data..."
                  : "Loading attendance data..."}
              </h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we fetch the information
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
      {/* Header */}
      <div className="flex items-left gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/attendance")}
          className="h-9 w-9 p-0"
          disabled={isLoading}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditMode ? "Edit Attendance" : "Add Attendance"}
          </h1>
          <p className="text-muted-foreground">
            {employee
              ? `${isEditMode ? "Update" : "Add"} attendance for ${
                  employee.fullName
                } - ${month}/${year}`
              : `${isEditMode ? "Update" : "Add"} attendance details`}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">Error</p>
          </div>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Inactive Employee Info */}
      {employee && !employee.isActive && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            <p className="text-sm font-medium text-blue-800">
              Inactive Employee - Limited Access
            </p>
          </div>
          <p className="mt-1 text-sm text-blue-700">
            This employee is marked as inactive
            {employee.resignationDate &&
              ` (resigned on ${new Date(
                employee.resignationDate
              ).toLocaleDateString()})`}
            .{" "}
            {isEditMode
              ? "You can edit existing attendance records."
              : "New attendance can only be added if the employee resigned during or after this month."}
          </p>
        </div>
      )}

      {/* Employee Info Card */}
      {employee && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Employee Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Name</p>
                <p className="font-medium">{employee.fullName}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Email</p>
                <p className="font-medium">{employee.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Designation</p>
                <p className="font-medium">{employee.designation || "N/A"}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <div className="mt-1">
                  {employee.isActive ? (
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-green-100 text-green-800">
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-100 text-red-800">
                      Inactive
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attendance Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Attendance Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="workingDays">Working Days</Label>
                <Input
                  id="workingDays"
                  type="text"
                  placeholder="Enter working days"
                  value={formData.workingDays}
                  onChange={(e) =>
                    handleInputChange("workingDays", e.target.value)
                  }
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Number of days employee actually worked
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="paidLeaves">Paid Leaves</Label>
                <Input
                  id="paidLeaves"
                  type="text"
                  placeholder="Enter paid leaves"
                  value={formData.paidLeaves}
                  onChange={(e) =>
                    handleInputChange("paidLeaves", e.target.value)
                  }
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Available: {employee?.remainingPaidLeaves || 0} days
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="unpaidLeaves">Unpaid Leaves</Label>
                <Input
                  id="unpaidLeaves"
                  type="text"
                  placeholder="Enter unpaid leaves"
                  value={formData.unpaidLeaves}
                  onChange={(e) =>
                    handleInputChange("unpaidLeaves", e.target.value)
                  }
                  disabled={isLoading}
                />
                <p className="text-xs text-muted-foreground">
                  Leave without pay days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calculated Values */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Attendance Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-600 font-medium">Total Days</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">
                  {calculatedValues.totalDays}
                </p>
                <p className="text-xs text-blue-600 mt-1">Working + Leaves</p>
              </div>
              <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                <p className="text-sm text-green-600 font-medium">
                  Present Days
                </p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {calculatedValues.presentDays}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Working + Paid Leaves
                </p>
              </div>
              <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                <p className="text-sm text-purple-600 font-medium">
                  Remaining Paid Leaves
                </p>
                <p className="text-2xl font-bold text-purple-900 mt-1">
                  {calculatedValues.remainingPaidLeaves}
                </p>
                <p className="text-xs text-purple-600 mt-1">After this month</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/attendance")}
            disabled={isLoading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading} className="flex-1">
            {isLoading ? (
              <>
                <Spinner variant="ellipsis" size={16} className="mr-2" />
                {isEditMode ? "Updating..." : "Adding..."}
              </>
            ) : (
              <>{isEditMode ? "Update Attendance" : "Add Attendance"}</>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
